import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { AdminAPI } from '@shared/types/api';
import { api } from '@/lib/api';

export function useAdminNeighborhoods(options: { includeRestaurants?: boolean } = {}) {
  const { includeRestaurants = true } = options;

  return useQuery<AdminAPI.GetNeighborhoodsDataResponse>({
    queryKey: ['admin', 'neighborhoods', { includeRestaurants }],
    queryFn: async () => {
      const suffix = includeRestaurants ? '' : '?includeRestaurants=false';
      return await api.get(`/admin/neighborhoods${suffix}`);
    },
  });
}

export interface AdminRestaurantFilters {
  page?: number;
  limit?: number;
  search?: string;
  cityId?: string | null;
  neighborhoodId?: string | null;
  type?: string | null;
}

// Server-side filtered & paginated restaurants list
export function useAdminRestaurants(filters: AdminRestaurantFilters = {}) {
  const { page = 1, limit = 50, search = '', cityId, neighborhoodId, type } = filters;

  return useQuery<AdminAPI.GetRestaurantsResponse>({
    queryKey: ['admin', 'restaurants', 'list', { page, limit, search, cityId, neighborhoodId, type }],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (search.trim()) params.set('search', search.trim());
      if (neighborhoodId) params.set('neighborhoodId', neighborhoodId);
      else if (cityId) params.set('cityId', cityId);
      if (type) params.set('type', type);
      return await api.get(`/admin/neighborhoods/restaurants?${params.toString()}`);
    },
  });
}

// Get all static cities (master list for city selection)
interface StaticCity {
  id: string;
  name: string;
  emoji: string;
  timezone: string;
  localLanguage?: string;
}

interface StaticCitiesResponse {
  success: boolean;
  data?: StaticCity[];
  error?: string;
}

export function useStaticCities() {
  return useQuery<StaticCitiesResponse>({
    queryKey: ['admin', 'static-cities'],
    queryFn: async () => {
      return await api.get('/admin/neighborhoods/static-cities');
    },
  });
}

// Bounds for viewport-restricted search (southwest and northeast corners)
export interface SearchBounds {
  sw: { lat: number; lng: number };
  ne: { lat: number; lng: number };
}

// Google Maps search hook
export function useGoogleMapsSearch() {
  return useMutation<
    AdminAPI.GoogleMapsSearchResponse,
    Error,
    { query: string; lat?: number; lng?: number; bounds?: SearchBounds }
  >({
    mutationFn: async ({ query, lat, lng, bounds }) => {
      const params = new URLSearchParams({ query });
      if (bounds) {
        params.append('swLat', bounds.sw.lat.toString());
        params.append('swLng', bounds.sw.lng.toString());
        params.append('neLat', bounds.ne.lat.toString());
        params.append('neLng', bounds.ne.lng.toString());
      } else {
        if (lat !== undefined) params.append('lat', lat.toString());
        if (lng !== undefined) params.append('lng', lng.toString());
      }
      return await api.get(`/admin/google-maps/search?${params.toString()}`);
    },
  });
}

// Google Maps place details hook
export function useGoogleMapsPlaceDetails() {
  return useMutation<AdminAPI.GoogleMapsPlaceDetailsResponse, Error, string>({
    mutationFn: async (placeId) => {
      return await api.get(`/admin/google-maps/place/${placeId}`);
    },
  });
}

export function useCreateCity() {
  const queryClient = useQueryClient();
  
  return useMutation<AdminAPI.CreateCityResponse, Error, AdminAPI.CreateCityRequest>({
    mutationFn: async (data) => {
      return await api.post('/admin/neighborhoods/cities', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'neighborhoods'] });
    },
  });
}

export function useCreateNeighborhood() {
  const queryClient = useQueryClient();
  
  return useMutation<AdminAPI.CreateNeighborhoodResponse, Error, AdminAPI.CreateNeighborhoodRequest>({
    mutationFn: async (data) => {
      return api.post('/admin/neighborhoods/neighborhoods', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'neighborhoods'] });
    },
  });
}

export function useCreateRestaurant() {
  const queryClient = useQueryClient();
  
  return useMutation<AdminAPI.CreateRestaurantResponse, Error, AdminAPI.CreateRestaurantRequest>({
    mutationFn: async (data) => {
      return await api.post('/admin/neighborhoods/restaurants', data);

    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'neighborhoods'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'restaurants', 'list'] });
    },
  });
}

export function useUpdateCity() {
  const queryClient = useQueryClient();
  
  return useMutation<AdminAPI.UpdateCityResponse, Error, { id: string; data: AdminAPI.UpdateCityRequest }>({
    mutationFn: async ({ id, data }) => {
      return await api.patch(`/admin/neighborhoods/cities/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'neighborhoods'] });
    },
  });
}

export function useUpdateNeighborhood() {
  const queryClient = useQueryClient();
  
  return useMutation<AdminAPI.UpdateNeighborhoodResponse, Error, { id: string; data: AdminAPI.UpdateNeighborhoodRequest }>({
    mutationFn: async ({ id, data }) => {
      return await api.patch(`/admin/neighborhoods/neighborhoods/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'neighborhoods'] });
    },
  });
}

export function useUpdateRestaurant() {
  const queryClient = useQueryClient();
  
  return useMutation<AdminAPI.UpdateRestaurantResponse, Error, { id: string; data: AdminAPI.UpdateRestaurantRequest }>({
    mutationFn: async ({ id, data }) => {
      return await api.patch(`/admin/neighborhoods/restaurants/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'neighborhoods'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'restaurants', 'list'] });
    },
  });
}

// Get per-restaurant feedback stats (NPS and rating distribution)
export function useRestaurantStats() {
  return useQuery<AdminAPI.GetRestaurantStatsResponse>({
    queryKey: ['admin', 'restaurants', 'stats'],
    queryFn: async () => {
      return await api.get('/admin/restaurants/stats');
    },
  });
}

